import React, { useEffect, useState } from 'react';

import { ErrorMessages } from '../../components/error/errorMessages';
import { Loader, LoadingView } from '../../components/common/loader';
import { authSelectedPharmacist } from '../../services/apis/auth/selectedPharmacistID';
import { fetchPharmacistOfPharmacy } from '../../services/apis/dispense/pharmacistOfPharmacy';
import { saveInstoreDefaultPharmacist } from '../../services/apis/auth/saveInstoreDefaultPharmacist';
import { useAllSelector } from '../../services/selectors/allSelector';
import { useAuthSelector } from '../../services/selectors/authSelector';
import { findPharmacistById, getPharmacistDisplayName, idsMatch } from '../../utils/helper/helper';

import * as Common from '../../components/common';
import * as Hooks from '../../hooks';
import * as routeNames from '../../routes/routeName';

const SelectDefaultPharmacist = () => {
	const dispatch = Hooks.useCustomDispatch();
	const navigate = Hooks.useCustomNavigate();

	const { pharmacyId, saveInstoreDefaultPharmacistResponse } = useAuthSelector();
	const {
		pharmacistDetail,
		pharmacistOfPharmacyListResponse,
		driverDefaultPharmacistId,
	} = useAllSelector();

	const [selectedPharmacistId, setSelectedPharmacistId] = useState('');
	const [submitError, setSubmitError] = useState('');

	const defaultPharmacist = findPharmacistById(pharmacistDetail, driverDefaultPharmacistId);
	const isSaving = saveInstoreDefaultPharmacistResponse?.loading;
	const hasPharmacists = pharmacistDetail?.length > 0;

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (pharmacyId) {
			dispatch(fetchPharmacistOfPharmacy(pharmacyId));
		}
		return () => { }
	}, [dispatch, pharmacyId]);

	useEffect(() => {
		if (!selectedPharmacistId && defaultPharmacist?.id) {
			setSelectedPharmacistId(defaultPharmacist.id);
		}
		return () => { }
	}, [defaultPharmacist?.id, selectedPharmacistId]);

	const handlePharmacistSelect = (id) => {
		setSelectedPharmacistId(id);
		setSubmitError('');
	};

	const handleContinue = async () => {
		if (!selectedPharmacistId) {
			setSubmitError('Please select a pharmacist to continue.');
			return;
		}

		const response = await dispatch(saveInstoreDefaultPharmacist({
			pharmacy_id: pharmacyId,
			pharmacist_id: selectedPharmacistId
		}));

		if (saveInstoreDefaultPharmacist.fulfilled.match(response)) {
			dispatch(authSelectedPharmacist(selectedPharmacistId));
			dispatch(fetchPharmacistOfPharmacy(pharmacyId));
			navigate(routeNames.DASHBOARD_PHARMACY);
			return;
		}

		setSubmitError('Oops! Got an issue while saving pharmacist. Please try again.');
	};

	return (
		<div className="app-wrapper flex justify-center h-screen">
			<div className="app-screen no-shades w-full h-full overflow-x-hidden">
				<div className="relative p-4">
					{pharmacistOfPharmacyListResponse?.loading &&
						<div className="progress-loader">
							<div className="progress"></div>
						</div>
					}

					<div className="card bg-white p-4 rounded-lg shadow-sm">
						<Common.TopBar pharmacyId={pharmacyId} />

						<div className="mt-4 rounded-md border border-green-100 bg-green-50 px-3 py-3">
							<h1 className="text-sm font-bold text-green-900">Select Today's Default Pharmacist</h1>
							{defaultPharmacist &&
								<p className="mt-1 text-xs font-medium text-green-800">
									Current default: {getPharmacistDisplayName(defaultPharmacist)}
								</p>
							}
						</div>

						{pharmacistOfPharmacyListResponse?.loading && !hasPharmacists ?
							<div className="flex items-center justify-center py-6">
								<LoadingView />
							</div>
							:
							pharmacistOfPharmacyListResponse?.error !== null ?
								<div className="flex items-center justify-center py-6">
									<ErrorMessages error={'Oops! Got an issue while fetching pharmacist details. Please try to reload the page again.'} />
								</div>
								:
								!hasPharmacists ?
									<div className="py-6">
										<p className="text-sm font-medium text-gray-700">No pharmacist is available right now.</p>
									</div>
									:
									<div className="shadow-md grid lg:grid-cols-2 md:grid-cols-2 grid-cols-1 border border-gray-100 my-4 rounded-md p-2">
										{pharmacistDetail?.map((value, index) => (
											<div key={value?.id || index} className="bg-white border border-gray-100 m-1 p-2 rounded-md">
												<label htmlFor={`default-pharmacist-${index}`} className="cursor-pointer flex items-center gap-2">
													<div className="w-6 h-6 bg-gray-200 rounded-full overflow-hidden">
														<img className="w-full h-full object-cover" alt="" src={value?.cover_photo || value?.photo} />
													</div>
													<div className="flex-grow">
														<h1 className="text-sm font-medium text-gray-900">{getPharmacistDisplayName(value)}</h1>
													</div>
													<div className="flex-shrink-0">
														<input
															id={`default-pharmacist-${index}`}
															type="radio"
															value={value.id}
															name="default-pharmacist-selection"
															className="peer"
															onChange={() => handlePharmacistSelect(value.id)}
															checked={idsMatch(selectedPharmacistId, value.id)}
														/>
													</div>
												</label>
											</div>
										))}
									</div>
						}

						{submitError &&
							<p className="mb-3 text-sm font-medium text-red-600">{submitError}</p>
						}

						<button
							type="button"
							onClick={isSaving ? null : handleContinue}
							disabled={isSaving || !hasPharmacists}
							className={`text-white w-full flex items-center justify-center focus:ring-4 focus:ring-blue-300 font-bold rounded-lg text-lg px-5 py-3 ${selectedPharmacistId && hasPharmacists ? 'bg-blue-700 hover:bg-blue-800' : 'bg-blue-100'}`}
						>
							{isSaving ? <Loader /> : 'Continue'}
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default SelectDefaultPharmacist
